import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';

export interface ExportJobCardState {
  jobOid: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  progress: number; // 0-100
  estimatedSecondsRemaining: number | null;
  deviceLabel: string;
  deviceCount: number;
  processedDataPoints: number;
  totalDataPoints: number;
  fileSizeMb: number | null;
  errorMessage: string | null;
  createdAt: Date;
  startedAt: Date | null;
  downloadUrl?: string | null;
  startDate: string;
  endDate: string;
  granularity: string;
  /** Energy preset the export was created with; null/undefined means "all data". */
  energyPreset?: string | null;
  /** Human-readable download filename: `{ldn-or-label}-{DDMMYYYY}.csv`. */
  fileName?: string;
}

@Component({
  selector: 'xemex-export-job-card',
  standalone: true,
  imports: [CommonModule, MatProgressBarModule, MatIconModule, MatTooltipModule, TranslateModule],
  template: `
    <div class="export-job-card" [ngClass]="'export-job-card--' + job().status">
      <div class="export-job-card__header">
        <span class="export-job-card__device-label">{{ job().deviceLabel }}</span>
        @if (job().deviceCount > 1) {
          <span class="export-job-card__device-count">({{ job().deviceCount }} {{ 'dataExport.devices' | translate }})</span>
        }
      </div>

      <div class="export-job-card__progress-section">
        <mat-progress-bar
          [value]="job().progress"
          [ngClass]="'progress-bar--' + job().status"
          [attr.aria-label]="job().progress + '%'"
        />
        <div class="export-job-card__progress-labels">
          <span class="export-job-card__percentage">{{ job().progress | number: '1.0-0' }}%</span>
          <span class="export-job-card__eta">{{ formatEta() }}</span>
        </div>
      </div>

      <div class="export-job-card__metadata">
        @if (job().totalDataPoints > 0) {
          <small class="export-job-card__data-points">
            {{ 'dataExport.dataPoints.processed' | translate }}
            {{ job().processedDataPoints | number }} / {{ job().totalDataPoints | number }}
          </small>
        }
        @if (job().fileSizeMb !== null) {
          <small class="export-job-card__file-size">
            {{ 'dataExport.estimatedSize' | translate: { size: job().fileSizeMb } }}
          </small>
        }
        @if (job().errorMessage && job().status === 'failed') {
          <small class="export-job-card__error" [title]="job().errorMessage">
            {{ job().errorMessage | slice: 0: 80 }}
          </small>
        }
      </div>
    </div>
  `,
  styleUrl: './export-job-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExportJobCardComponent {
  job = input.required<ExportJobCardState>();

  protected formatEta = computed(() => {
    const remaining = this.job().estimatedSecondsRemaining;
    const status = this.job().status;

    if (status === 'queued') {
      return this.formatDate(this.job().createdAt);
    }

    if (status === 'completed') {
      return this.formatDate(this.job().createdAt);
    }

    if (status === 'failed' || !remaining) {
      return '';
    }

    if (remaining > 3600) {
      const hours = Math.floor(remaining / 3600);
      const mins = Math.floor((remaining % 3600) / 60);
      return `~${hours}h ${mins}m`;
    }

    if (remaining > 60) {
      const mins = Math.floor(remaining / 60);
      return `~${mins}m`;
    }

    return `~${remaining}s`;
  });

  private formatDate(date: Date): string {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  }
}
